import { CanvasGlyphRasterizer, CanvasRenderer } from './canvas-renderer'
import { parseGridConfig, type GridFontVariant, type GridRendererConfig } from './config'
import { FontStore } from './font-store'
import { buildGeometry, type GridGeometry } from './geometry'
import {
  buildImageGeometry,
  EMPTY_IMAGE_DRAWS,
  type ImageDraw,
  type ImageGeometry,
  type ImageGeometryAssetSource,
  type ImageGeometryRenderAsset,
} from './image-geometry'
import { GridInput, type GridInputSelection, type GridInputTransport } from './input'
import {
  BrowserImageProtocolError,
  BrowserImageStaleOperationError,
  BrowserImageStore,
  GRID_IMAGE_ENCODED_BUDGET_BYTES,
  GRID_IMAGE_MAX_ASSETS,
  type BrowserImageLease,
  type BrowserImageStoreOptions,
  type GridImageStore,
  type ImageRepresentationStore,
} from './image-store'
import {
  ImagePresentationCoordinator,
  ImagePresentationError,
} from './image-presentation'
import { GridModel, type GridRecoveryReason } from './model'
import { GridPointer, type GridPointerSelection, type GridPointerTransport } from './pointer'
import { GridSelectionController, type GridSelectionTransport } from './selection'
import {
  GridChunkAssembler,
  GridImageCapacityError,
  GridProtocolError,
  GRID_COLS_MAX,
  GRID_COLS_MIN,
  GRID_ROWS_MAX,
  GRID_ROWS_MIN,
  type DecodedFontRecord,
  type DecodedGridRecord,
  type DecodedGridStreamRecord,
  type ImageAssetReservation,
} from './protocol'
import { WebGLRenderer } from './webgl-renderer'
import type {
  GridCancelInteractionMessage,
  GridFeatureReadyMessage,
  GridImageRecoveryReason,
  GridImageResetMessage,
  GridPointerResultMessage,
  GridReadyMessage,
  GridRole,
  GridRoleMessage,
} from '../websocket'

const MIN_COLS = GRID_COLS_MIN
const MAX_COLS = GRID_COLS_MAX
const MIN_ROWS = GRID_ROWS_MIN
const MAX_ROWS = GRID_ROWS_MAX

export interface GridSocketControls
    extends GridInputTransport, GridPointerTransport, GridSelectionTransport {
  sendGridResize(configGeneration: number, cols: number, rows: number): void
  sendGridRecover(lastSequence: number, reason: GridRecoveryReason): void
  sendGridTakeControl(configGeneration: number, cols: number, rows: number): void
  sendGridFeature(feature: 'image-v1'): void
  sendGridImageRecover(
    assetEpoch: number,
    lastPresentationGeneration: number,
    reason: GridImageRecoveryReason,
  ): void
}

export interface GridFontStore {
  configure(config: GridRendererConfig): Promise<void>
  accept(record: DecodedFontRecord): Promise<void>
  isReady(configGeneration: number): boolean
  familyFor(variant: 0 | 1 | 2 | 3, configGeneration: number): string
  availableVariants(configGeneration: number): ReadonlySet<GridFontVariant>
  dispose(): void
}

export interface GridRenderFrame {
  readonly geometry: GridGeometry
  readonly images: readonly ImageDraw[]
  readonly config: GridRendererConfig
  readonly cols: number
  readonly rows: number
  readonly dpr: number
  readonly viewportWidth?: number
  readonly viewportHeight?: number
}

export interface GridRenderer {
  acquireCachedImage?(assetId: number): BrowserImageLease<ImageGeometryRenderAsset> | null
  render(frame: GridRenderFrame): Promise<boolean>
  dispose(): void
}

export interface GridViewOptions {
  readonly container: HTMLElement
  readonly socket: GridSocketControls
  readonly resizeDebounceMs?: number
  readonly devicePixelRatio?: () => number
  readonly createFontStore?: () => GridFontStore
  readonly imageSupport?: boolean
  readonly createImageStore?: (options: BrowserImageStoreOptions) => GridImageStore
  readonly createWebGLRenderer?: (
    canvas: HTMLCanvasElement,
    fonts: GridFontStore,
    onFallback: (error: unknown) => void,
  ) => GridRenderer
  readonly createCanvasRenderer?: (canvas: HTMLCanvasElement, fonts: GridFontStore) => GridRenderer
  readonly onRoleChange?: (role: GridRole | null) => void
  readonly onInteractionReset?: () => void
  readonly onCanvasChange?: (canvas: HTMLCanvasElement) => void
  readonly inputSelection?: GridInputSelection | null
  readonly pointerSelection?: GridPointerSelection | null
  readonly onClipboardError?: (message: string) => void
}

interface ActiveConfiguration {
  readonly value: GridRendererConfig
  readonly configure: Promise<void>
  readonly requiredStreamVariants: ReadonlySet<number>
  readonly receivedStreamVariants: Set<number>
  readonly fontTasks: Set<Promise<void>>
}

function boundedInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 0xffffffff
}

function validRole(value: unknown): value is GridRole {
  return value === 'controller' || value === 'observer'
}

function validFeatures(value: unknown): value is 'image-v1'[] | undefined {
  return value === undefined || (Array.isArray(value) && value.length <= 1 &&
    value.every((feature) => feature === 'image-v1'))
}

function validImageResetReason(value: unknown): boolean {
  return value === 'assets_revoked' || value === 'generation_exhausted' || value === 'capacity'
}

function imageChunkKind(message: ArrayBuffer | Uint8Array): boolean {
  const bytes = message instanceof Uint8Array ? message : new Uint8Array(message)
  return bytes.length > 5 && bytes[5] >= 3 && bytes[5] <= 5
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value))
}

function fontVariantNumber(variant: GridFontVariant): 0 | 1 | 2 | 3 {
  switch (variant) {
    case 'regular': return 0
    case 'bold': return 1
    case 'italic': return 2
    case 'bold-italic': return 3
  }
}

function safeDpr(provider: () => number): number {
  const value = provider()
  return Number.isFinite(value) && value > 0 ? value : 1
}

type ImageDeliveryRecord = Exclude<DecodedGridStreamRecord, { kind: 'font' }>

interface ImageDeliveryItem {
  readonly record: ImageDeliveryRecord
  readonly reservation?: ImageAssetReservation
  readonly complete: () => void
}

// One full server synchronization can place all retained assets ahead of at
// most one grid, one scene, and one barrier. Anything beyond that while an
// asset verification is stalled is no longer a bounded atomic presentation.
const IMAGE_DELIVERY_NON_ASSET_LIMIT = 3

export class GridView {
  readonly #container: HTMLElement
  readonly #socket: GridSocketControls
  readonly #resizeDebounceMs: number
  readonly #devicePixelRatio: () => number
  readonly #createFontStore: () => GridFontStore
  readonly #createWebGLRenderer: GridViewOptions['createWebGLRenderer']
  readonly #createCanvasRenderer: GridViewOptions['createCanvasRenderer']
  readonly #onRoleChange?: (role: GridRole | null) => void
  readonly #onInteractionReset?: () => void
  readonly #onCanvasChange?: (canvas: HTMLCanvasElement) => void
  readonly #resizeObserver: ResizeObserver
  #canvas: HTMLCanvasElement
  #assembler: GridChunkAssembler<true>
  #model: GridModel
  #fontStore: GridFontStore
  readonly #imageSupport: boolean
  readonly #imageStore: GridImageStore
  readonly #imagePresentation: ImagePresentationCoordinator
  #renderer: GridRenderer
  #input: GridInput | null = null
  #pointer: GridPointer | null = null
  #selection: GridSelectionController | null = null
  #webglRasterizer: CanvasGlyphRasterizer | null = null
  #rendererKind: 'webgl' | 'canvas2d' = 'webgl'
  #configuration: ActiveConfiguration | null = null
  #announcedConfigGeneration = 0
  #role: GridRole | null = null
  #ownershipGeneration = 0
  #minimumResizeGeneration = 0
  #ready = false
  #recovering = false
  #imageFeatureRequested = false
  #imageFeatureReady = false
  #imageRecovering = false
  #imageCapacityBlocked = false
  #deferredImageRecoveryReason: GridImageRecoveryReason | null = null
  #assetEpoch = 0
  #cssWidth = 0
  #cssHeight = 0
  #lastDpr: number
  #dprMediaQuery: MediaQueryList | null = null
  #lastResizeKey: string | null = null
  #resizeTimer: number | null = null
  #renderEpoch = 0
  #renderSession = 0
  #renderPump: Promise<void> | null = null
  #renderDirty = false
  #imageDeliverySession = 0
  readonly #imageDeliveryInbox: ImageDeliveryItem[] = []
  readonly #activeImageDeliveries = new Set<ImageDeliveryItem>()
  #imageDeliveryPump: Promise<void> | null = null
  #imageDeliveryOverflowed = false
  #queuedImageAssetCount = 0
  #queuedImageAssetBytes = 0
  #queuedImageNonAssetCount = 0
  #activeImageAssetCount = 0
  #activeImageAssetBytes = 0
  #disposed = false
  readonly #activePointers = new Set<number>()
  readonly #handleDprMediaChange = (): void => {
    if (this.#disposed) return
    const changed = this.#updateDpr()
    this.#armDprObserver()
    if (changed) void this.#renderLatest()
  }
  readonly #handleWindowResize = (): void => {
    if (this.#disposed || !this.#updateDpr()) return
    this.#armDprObserver()
    void this.#renderLatest()
  }
  readonly #handleImageCapacityAvailable = (): void => {
    if (this.#disposed || !this.#imageFeatureReady || this.#assetEpoch === 0) return
    const deferredReason = this.#deferredImageRecoveryReason
    if (deferredReason !== null) {
      this.#deferredImageRecoveryReason = null
      this.#imageCapacityBlocked = false
      this.#enterImageRecovery(deferredReason)
      return
    }
    if (!this.#imageCapacityBlocked) return
    this.#imageCapacityBlocked = false
    this.#enterImageRecovery('asset')
  }

  constructor(options: GridViewOptions) {
    this.#container = options.container
    this.#socket = options.socket
    this.#resizeDebounceMs = options.resizeDebounceMs ?? 50
    this.#devicePixelRatio = options.devicePixelRatio ?? (() => window.devicePixelRatio || 1)
    this.#createFontStore = options.createFontStore ?? (() => new FontStore())
    this.#imageSupport = options.imageSupport ?? true
    const imageStoreOptions: BrowserImageStoreOptions = {
      onStateChange: (event) => {
        if (event.state !== 'pending') this.requestRepaint()
      },
      onDiagnostic: (message) => console.error(`[lera] ${message}`),
    }
    this.#imageStore = options.createImageStore?.(imageStoreOptions) ??
      new BrowserImageStore(imageStoreOptions)
    this.#imagePresentation = new ImagePresentationCoordinator(this.#imageStore)
    this.#imageStore.setCapacityListener(this.#handleImageCapacityAvailable)
    this.#assembler = this.#newAssembler()
    this.#createWebGLRenderer = options.createWebGLRenderer
    this.#createCanvasRenderer = options.createCanvasRenderer
    this.#onRoleChange = options.onRoleChange
    this.#onInteractionReset = options.onInteractionReset
    this.#onCanvasChange = options.onCanvasChange
    this.#lastDpr = safeDpr(this.#devicePixelRatio)
    this.#fontStore = this.#createFontStore()
    this.#model = this.#newModel()
    this.#canvas = this.#newCanvas()
    this.#container.appendChild(this.#canvas)
    this.#renderer = this.#newWebGLRenderer()
    // One controller serves both seams: the pointer decides when a drag becomes
    // a selection, the input line decides what Ctrl+C and Escape do to it.
    this.#selection = new GridSelectionController({
      transport: this.#socket,
      grid: () => this.#model,
      resizeGeneration: () => this.#minimumResizeGeneration,
      onClipboardError: options.onClipboardError,
      onChange: () => { void this.#renderLatest() },
    })
    this.#input = new GridInput({
      surface: this.#canvas,
      transport: this.#socket,
      enabled: () => this.#ready && this.#role === 'controller' && this.#configuration !== null,
      selection: options.inputSelection ?? this.#selection,
    })
    this.#pointer = new GridPointer({
      surface: this.#canvas,
      transport: this.#socket,
      geometry: { cols: () => this.#model.cols, rows: () => this.#model.rows },
      enabled: () => this.#ready && this.#role === 'controller' && this.#configuration !== null,
      selection: options.pointerSelection ?? this.#selection,
    })
    this.#resizeObserver = new ResizeObserver((entries) => this.#observeResize(entries))
    this.#resizeObserver.observe(this.#container)
    window.addEventListener('resize', this.#handleWindowResize)
    this.#armDprObserver()
  }

  get canvas(): HTMLCanvasElement { return this.#canvas }
  get model(): GridModel { return this.#model }
  get config(): GridRendererConfig | null { return this.#configuration?.value ?? null }
  get role(): GridRole | null { return this.#role }
  get selection(): GridSelectionController | null { return this.#selection }
  get rendererKind(): 'webgl' | 'canvas2d' { return this.#rendererKind }
  get committedImageSceneGeneration(): number {
    return this.#imagePresentation.committedSceneGeneration
  }
  get committedImagePlacementCount(): number {
    return this.#imagePresentation.committedPlacementCount
  }
  get lastImagePresentationGeneration(): number {
    return this.#imagePresentation.lastPresentationGeneration
  }
  get imageDeliveryQueuedCount(): number { return this.#imageDeliveryInbox.length }
  get imageDeliveryActiveCount(): number { return this.#activeImageDeliveries.size }
  get imageDeliveryOverflowed(): boolean { return this.#imageDeliveryOverflowed }

  handleReady(message: GridReadyMessage): boolean {
    if (this.#disposed || message.protocol !== 'grid-v1' || !validRole(message.role) ||
        !boundedInteger(message.config_generation) || !boundedInteger(message.resize_generation) ||
        !boundedInteger(message.ownership_generation) || !validFeatures(message.features)) {
      return false
    }
    this.#ready = true
    this.#announcedConfigGeneration = message.config_generation
    this.#ownershipGeneration = message.ownership_generation
    this.#minimumResizeGeneration = message.resize_generation
    this.#setRole(message.role)
    if (this.#imageSupport && message.features?.includes('image-v1') === true &&
        !this.#imageFeatureRequested) {
      this.#imageFeatureRequested = true
      this.#socket.sendGridFeature('image-v1')
    }
    return true
  }

  handleFeatureReady(message: GridFeatureReadyMessage): boolean {
    if (this.#disposed || !this.#ready || !this.#imageSupport || !this.#imageFeatureRequested ||
        this.#imageFeatureReady || message.type !== 'grid_feature_ready' ||
        message.feature !== 'image-v1' || !boundedInteger(message.asset_epoch)) return false
    this.#assetEpoch = message.asset_epoch
    this.#invalidateImageDelivery()
    this.#imageStore.reset(message.asset_epoch)
    this.#imagePresentation.activate(message.asset_epoch)
    this.#imageFeatureReady = true
    this.#imageRecovering = false
    this.#imageCapacityBlocked = false
    this.#deferredImageRecoveryReason = null
    this.#assembler.enableImageV1()
    return true
  }

  handleImageReset(message: GridImageResetMessage): void {
    if (this.#disposed || !this.#imageFeatureReady || message.type !== 'grid_image_reset' ||
        !boundedInteger(message.asset_epoch) || message.asset_epoch <= this.#assetEpoch ||
        !validImageResetReason(message.reason)) return
    this.#assetEpoch = message.asset_epoch
    // A Canvas render may own a bitmap lease while suspended on glyph work.
    // Invalidate it before reset closes the old epoch's bitmap; the repaint
    // below then cancels its renderer transaction before it can draw again.
    ++this.#renderEpoch
    ++this.#renderSession
    this.#renderPump = null
    this.#renderDirty = false
    this.#invalidateImageDelivery()
    this.#imageStore.reset(message.asset_epoch)
    this.#imagePresentation.activate(message.asset_epoch)
    this.#assembler.resetForRecovery()
    this.#imageRecovering = false
    this.#imageCapacityBlocked = false
    this.#deferredImageRecoveryReason = null
    this.requestRepaint()
  }

  handleRole(message: GridRoleMessage): void {
    if (!this.#ready || !validRole(message.role) || !boundedInteger(message.ownership_generation) ||
        !boundedInteger(message.config_generation) || !boundedInteger(message.resize_generation) ||
        message.ownership_generation < this.#ownershipGeneration ||
        message.resize_generation < this.#minimumResizeGeneration ||
        (this.#configuration !== null &&
         message.config_generation !== this.#configuration.value.configGeneration)) return
    const ownershipChanged = message.ownership_generation > this.#ownershipGeneration
    this.#ownershipGeneration = message.ownership_generation
    this.#minimumResizeGeneration = message.resize_generation
    this.#announcedConfigGeneration = message.config_generation
    this.#setRole(message.role)
    if (ownershipChanged) this.#clearImagePresentationForSynchronization()
    this.#scheduleResize()
  }

  async handleConfig(message: unknown): Promise<void> {
    if (!this.#ready) return
    let value: GridRendererConfig
    try {
      value = parseGridConfig(message)
    } catch {
      this.#enterRecovery('generation')
      return
    }
    if (this.#configuration === null && value.configGeneration < this.#announcedConfigGeneration) return
    if (this.#configuration !== null &&
        value.configGeneration <= this.#configuration.value.configGeneration) return
    if (this.#configuration === null && value.configGeneration !== this.#announcedConfigGeneration) {
      this.#enterRecovery('generation')
      return
    }
    const configure = this.#fontStore.configure(value)
    const requiredStreamVariants = new Set<number>()
    for (const font of value.fonts) {
      if (!font.embedded) requiredStreamVariants.add(fontVariantNumber(font.variant))
    }
    const active: ActiveConfiguration = {
      value,
      configure,
      requiredStreamVariants,
      receivedStreamVariants: new Set(),
      fontTasks: new Set(),
    }
    if (this.#configuration !== null) this.#clearImagePresentationForSynchronization()
    this.#configuration = active
    this.#lastResizeKey = null
    this.#scheduleResize()
    try {
      await configure
      if (this.#configuration === active) await this.#renderLatest()
    } catch {
      if (this.#configuration === active) this.#enterRecovery('context')
    }
  }

  async handleBinary(message: ArrayBuffer | Uint8Array): Promise<void> {
    if (this.#disposed || !this.#ready) return
    if (this.#configuration === null) {
      this.#enterRecovery('generation')
      return
    }
    if (this.#imageFeatureReady && this.#imageDeliveryOverflowed) return
    if ((this.#imageCapacityBlocked || this.#deferredImageRecoveryReason !== null) &&
        imageChunkKind(message)) return
    let record: DecodedGridStreamRecord | null
    try {
      record = this.#assembler.push(message)
    } catch (error) {
      if (this.#imageFeatureReady && imageChunkKind(message)) {
        if (error instanceof GridImageCapacityError) {
          this.#imageCapacityBlocked = true
          return
        }
        const reason = error instanceof BrowserImageProtocolError
          ? error.reason
          : error instanceof GridProtocolError && error.code === 'gap' ? 'gap' : 'decode'
        this.#enterImageRecovery(reason)
        return
      }
      const reason = error instanceof GridProtocolError && error.code === 'gap' ? 'gap' : 'decode'
      this.#enterRecovery(reason)
      return
    }
    if (record === null) return
    if (record.kind === 'font') {
      await this.#acceptFont(record.font)
      return
    }
    if (this.#imageFeatureReady) {
      await this.#enqueueImageRecord(record)
      return
    }
    if (record.kind !== 'screen') {
      this.#enterRecovery('decode')
      return
    }
    this.#acceptScreen(record)
    await this.#renderLatest()
  }

  async #acceptDecodedRecord(record: DecodedGridStreamRecord): Promise<void> {
    if (record.kind === 'font') {
      await this.#acceptFont(record.font)
      return
    }
    if (record.kind === 'image-asset') {
      try {
        await this.#imagePresentation.acceptAsset(record.asset, record.reservation)
      } catch (error) {
        if (error instanceof BrowserImageStaleOperationError) return
        if (this.#imageDeliveryOverflowed) return
        this.#enterImageRecovery(error instanceof BrowserImageProtocolError ? error.reason : 'decode')
      }
      return
    }
    if (record.kind === 'image-scene') {
      if (this.#referencesMissingFutureGrid(record.scene.gridSequence)) {
        this.#enterRecovery('gap')
        return
      }
      try {
        this.#imagePresentation.stageScene(record.scene, this.#model)
      } catch (error) {
        this.#enterImageRecovery(error instanceof ImagePresentationError ? error.reason : 'context')
      }
      return
    }
    if (record.kind === 'image-barrier') {
      if (this.#referencesMissingFutureGrid(record.barrier.gridSequence)) {
        this.#enterRecovery('gap')
        return
      }
      if (this.#recovering && this.#imagePresentation.pendingGridSequence === 0) {
        this.#enterImageRecovery('gap')
        return
      }
      try {
        const committed = this.#imagePresentation.commit(record.barrier, this.#model)
        this.#model = committed.grid
        this.#minimumResizeGeneration = this.#model.resizeGeneration
        this.#recovering = false
        this.#imageRecovering = false
        this.#imageCapacityBlocked = false
        this.#deferredImageRecoveryReason = null
        void this.#renderLatest()
      } catch (error) {
        this.#enterImageRecovery(error instanceof ImagePresentationError ? error.reason : 'context')
      }
      return
    }
    if (this.#imageFeatureReady) {
      this.#acceptImageScreen(record)
      return
    }
    this.#acceptScreen(record)
    await this.#renderLatest()
  }

  async #enqueueImageRecord(record: ImageDeliveryRecord): Promise<void> {
    const reservation = record.kind === 'image-asset' ? record.reservation : undefined
    if (this.#disposed || this.#imageDeliveryOverflowed) {
      reservation?.release()
      return
    }
    await new Promise<void>((complete) => {
      const item: ImageDeliveryItem = {
        record,
        ...(reservation === undefined ? {} : { reservation }),
        complete,
      }
      if (!this.#imageDeliveryFits(item)) {
        reservation?.release()
        this.#dropImageDeliveryInbox()
        this.#imageDeliveryOverflowed = true
        complete()
        return
      }
      this.#imageDeliveryInbox.push(item)
      this.#accountQueuedImageDelivery(item, 1)
      this.#startImageDeliveryPump()
    })
  }

  #invalidateImageDelivery(): void {
    ++this.#imageDeliverySession
    this.#imageDeliveryPump = null
    this.#imageDeliveryOverflowed = false
    this.#dropImageDeliveryInbox()
  }

  #imageDeliveryFits(item: ImageDeliveryItem): boolean {
    if (item.record.kind === 'image-asset') {
      return this.#queuedImageAssetCount + this.#activeImageAssetCount < GRID_IMAGE_MAX_ASSETS &&
        item.record.asset.bytes.byteLength <=
          GRID_IMAGE_ENCODED_BUDGET_BYTES - this.#queuedImageAssetBytes -
            this.#activeImageAssetBytes
    }
    return this.#queuedImageNonAssetCount < IMAGE_DELIVERY_NON_ASSET_LIMIT
  }

  #accountQueuedImageDelivery(item: ImageDeliveryItem, direction: 1 | -1): void {
    if (item.record.kind === 'image-asset') {
      this.#queuedImageAssetCount += direction
      this.#queuedImageAssetBytes += direction * item.record.asset.bytes.byteLength
    } else {
      this.#queuedImageNonAssetCount += direction
    }
  }

  #dropImageDeliveryInbox(): void {
    for (const item of this.#imageDeliveryInbox) {
      item.reservation?.release()
      item.complete()
    }
    this.#imageDeliveryInbox.length = 0
    this.#queuedImageAssetCount = 0
    this.#queuedImageAssetBytes = 0
    this.#queuedImageNonAssetCount = 0
  }

  #startImageDeliveryPump(): void {
    if (this.#imageDeliveryPump !== null || this.#disposed) return
    const session = this.#imageDeliverySession
    // Install the pump before delivery begins: a synchronous protocol failure
    // may invalidate this session from inside the first record handler.
    const pump = Promise.resolve().then(async () => await this.#drainImageDeliveryInbox(session))
    this.#imageDeliveryPump = pump
    void pump.finally(() => {
      if (this.#imageDeliveryPump !== pump || session !== this.#imageDeliverySession) return
      this.#imageDeliveryPump = null
      if (this.#imageDeliveryInbox.length !== 0 && !this.#imageDeliveryOverflowed) {
        this.#startImageDeliveryPump()
      }
    })
  }

  async #drainImageDeliveryInbox(session: number): Promise<void> {
    while (!this.#disposed && session === this.#imageDeliverySession) {
      const item = this.#imageDeliveryInbox.shift()
      if (item === undefined) return
      this.#accountQueuedImageDelivery(item, -1)
      this.#activeImageDeliveries.add(item)
      if (item.record.kind === 'image-asset') {
        ++this.#activeImageAssetCount
        this.#activeImageAssetBytes += item.record.asset.bytes.byteLength
      }
      try {
        await this.#acceptDecodedRecord(item.record)
      } finally {
        this.#activeImageDeliveries.delete(item)
        if (item.record.kind === 'image-asset') {
          --this.#activeImageAssetCount
          this.#activeImageAssetBytes -= item.record.asset.bytes.byteLength
        }
        item.complete()
      }
      if (session !== this.#imageDeliverySession || this.#disposed) return
      if (this.#imageDeliveryOverflowed) {
        this.#enterRecovery('gap')
        return
      }
    }
  }

  takeControl(): void {
    const config = this.#configuration?.value
    if (this.#role !== 'observer' || config === undefined ||
        this.#cssWidth <= 0 || this.#cssHeight <= 0) return
    const size = this.#gridSize(config)
    this.#socket.sendGridTakeControl(config.configGeneration, size.cols, size.rows)
  }

  focus(): void { this.#input?.focus() }

  requestRepaint(): void { void this.#renderLatest() }

  setInputSelection(selection: GridInputSelection | null): void {
    this.#input?.setSelection(selection)
  }

  setPointerSelection(selection: GridPointerSelection | null): void {
    this.#pointer?.setSelection(selection)
  }

  handlePointerResult(message: GridPointerResultMessage): void {
    if (!this.#ready) return
    this.#pointer?.handlePointerResult(message)
  }

  // A server cancellation is authoritative for the ownership generation it
  // names; an older one refers to an interaction that is already gone.
  handleCancelInteraction(message: GridCancelInteractionMessage): void {
    if (!boundedInteger(message.ownership_generation) ||
        message.ownership_generation < this.#ownershipGeneration) return
    this.#pointer?.cancelFromServer()
    this.#selection?.invalidateFromServer()
  }

  resetForReconnect(): void {
    if (this.#disposed) return
    const restoreInputFocus = this.#input?.hasFocus() ?? false
    this.#cancelResize()
    ++this.#renderEpoch
    ++this.#renderSession
    this.#renderPump = null
    this.#renderDirty = false
    this.#releasePointerCapture()
    this.#pointer?.cancelInteraction()
    // The socket is gone, so there is nobody to send a clear to; the server
    // drops the pin itself when the controller disconnects.
    this.#selection?.invalidateFromServer()
    this.#input?.dispose()
    this.#pointer?.dispose()
    this.#onInteractionReset?.()
    this.#assembler.resetForRecovery()
    this.#assembler = this.#newAssembler()
    this.#model = this.#newModel()
    this.#invalidateImageDelivery()
    this.#configuration = null
    this.#ready = false
    this.#recovering = false
    this.#imageStore.resetForReconnect()
    this.#imagePresentation.reset()
    this.#imageFeatureRequested = false
    this.#imageFeatureReady = false
    this.#imageRecovering = false
    this.#imageCapacityBlocked = false
    this.#deferredImageRecoveryReason = null
    this.#assetEpoch = 0
    this.#ownershipGeneration = 0
    this.#minimumResizeGeneration = 0
    this.#announcedConfigGeneration = 0
    this.#lastResizeKey = null
    this.#setRole(null)
    this.#fontStore.dispose()
    this.#fontStore = this.#createFontStore()
    this.#renderer.dispose()
    this.#webglRasterizer?.dispose()
    this.#webglRasterizer = null
    this.#replaceCanvas(restoreInputFocus)
    this.#rendererKind = 'webgl'
    this.#renderer = this.#newWebGLRenderer()
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#cancelResize()
    ++this.#renderEpoch
    ++this.#renderSession
    this.#renderPump = null
    this.#renderDirty = false
    this.#resizeObserver.disconnect()
    window.removeEventListener('resize', this.#handleWindowResize)
    this.#clearDprObserver()
    this.#releasePointerCapture()
    this.#input?.dispose()
    this.#input = null
    this.#pointer?.dispose()
    this.#pointer = null
    this.#renderer.dispose()
    this.#webglRasterizer?.dispose()
    this.#webglRasterizer = null
    this.#fontStore.dispose()
    this.#assembler.resetForRecovery()
    this.#invalidateImageDelivery()
    this.#imageStore.dispose()
    this.#imagePresentation.reset()
    this.#canvas.remove()
  }

  #newModel(): GridModel {
    return new GridModel({ onRecoveryRequired: (reason) => this.#enterRecovery(reason) })
  }

  #newAssembler(): GridChunkAssembler<true> {
    return new GridChunkAssembler<true>({
      reserveImageAsset: (allocation) => this.#imageStore.reserve(allocation),
      activeImageEpoch: () => this.#imageStore.activeEpoch,
    })
  }

  #newCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    canvas.className = 'grid-canvas'
    canvas.tabIndex = 0
    canvas.spellcheck = false
    canvas.setAttribute('role', 'application')
    canvas.setAttribute('aria-label', 'Lera grid')
    canvas.addEventListener('pointerdown', (event) => this.#activePointers.add(event.pointerId), true)
    const release = (event: PointerEvent) => this.#activePointers.delete(event.pointerId)
    canvas.addEventListener('pointerup', release, true)
    canvas.addEventListener('pointercancel', release, true)
    return canvas
  }

  #newWebGLRenderer(): GridRenderer {
    const fallback = (error: unknown) => this.#switchToCanvas(error)
    if (this.#createWebGLRenderer !== undefined) {
      return this.#createWebGLRenderer(this.#canvas, this.#fontStore, fallback)
    }
    const rasterizer = new CanvasGlyphRasterizer(this.#fontStore)
    this.#webglRasterizer = rasterizer
    const imageStore = this.#imageStore.acquireRepresentation !== undefined &&
      this.#imageStore.discardRepresentation !== undefined
      ? this.#imageStore as ImageRepresentationStore
      : undefined
    return new WebGLRenderer(this.#canvas, rasterizer, {
      onFallbackRequested: fallback,
      onRestored: () => this.requestRepaint(),
      ...(imageStore === undefined ? {} : { imageStore }),
    })
  }

  #newCanvasRenderer(): GridRenderer {
    if (this.#createCanvasRenderer !== undefined) {
      return this.#createCanvasRenderer(this.#canvas, this.#fontStore)
    }
    return new CanvasRenderer(this.#canvas, this.#fontStore)
  }

  #replaceCanvas(restoreInputFocus?: boolean): void {
    const old = this.#canvas
    const restoreFocus = restoreInputFocus ??
      (this.#input?.hasFocus() ?? document.activeElement === old)
    const replacement = this.#newCanvas()
    old.replaceWith(replacement)
    this.#canvas = replacement
    this.#input?.attach(replacement)
    this.#pointer?.attach(replacement)
    this.#onCanvasChange?.(replacement)
    if (restoreFocus) this.#input?.focus()
  }

  #switchToCanvas(_error: unknown): void {
    if (this.#disposed || this.#rendererKind === 'canvas2d') return
    const prior = this.#renderer
    this.#rendererKind = 'canvas2d'
    ++this.#renderEpoch
    ++this.#renderSession
    this.#renderPump = null
    this.#renderDirty = false
    prior.dispose()
    this.#webglRasterizer?.dispose()
    this.#webglRasterizer = null
    this.#replaceCanvas()
    try {
      this.#renderer = this.#newCanvasRenderer()
      void this.#renderLatest()
    } catch {
      this.#enterRecovery('context')
    }
  }

  async #acceptFont(record: DecodedFontRecord): Promise<void> {
    const active = this.#configuration
    if (active === null || record.configGeneration !== active.value.configGeneration ||
        !active.requiredStreamVariants.has(record.variant) ||
        active.receivedStreamVariants.has(record.variant)) {
      this.#enterRecovery('generation')
      return
    }
    active.receivedStreamVariants.add(record.variant)
    let task: Promise<void>
    task = this.#fontStore.accept(record).then(
      () => { active.fontTasks.delete(task) },
      (error) => {
        active.fontTasks.delete(task)
        if (this.#configuration === active) this.#enterRecovery('context')
        throw error
      },
    )
    active.fontTasks.add(task)
    try {
      await task
      if (this.#configuration === active) await this.#renderLatest()
    } catch {
      // The font transaction already entered recovery above.
    }
  }

  #acceptScreen(record: Extract<DecodedGridRecord, { kind: 'screen' }>): void {
    const active = this.#configuration
    const frame = record.frame
    if (active === null || frame.configGeneration !== active.value.configGeneration ||
        active.receivedStreamVariants.size !== active.requiredStreamVariants.size) {
      this.#enterRecovery('generation')
      return
    }
    if (frame.resizeGeneration < this.#minimumResizeGeneration ||
        (this.#model.sequence !== 0 && frame.sequence <= this.#model.sequence)) return
    if (this.#recovering && frame.kind !== 'full') return
    try {
      if (frame.kind === 'full') {
        this.#model.applyFull(frame)
        this.#recovering = false
      } else {
        this.#model.applyPatch(frame)
      }
      this.#minimumResizeGeneration = frame.resizeGeneration
    } catch {
      // GridModel already reported the precise recovery reason.
    }
  }

  #acceptImageScreen(record: Extract<DecodedGridRecord, { kind: 'screen' }>): void {
    const active = this.#configuration
    const frame = record.frame
    if (active === null || frame.configGeneration !== active.value.configGeneration ||
        active.receivedStreamVariants.size !== active.requiredStreamVariants.size) {
      this.#enterRecovery('generation')
      return
    }
    if (frame.resizeGeneration < this.#minimumResizeGeneration ||
        (this.#model.sequence !== 0 && frame.sequence <= this.#model.sequence) ||
        (this.#recovering && frame.kind !== 'full')) {
      this.#enterRecovery(frame.resizeGeneration < this.#minimumResizeGeneration ? 'generation' : 'gap')
      return
    }
    try {
      this.#imagePresentation.stageGrid(frame, this.#model)
    } catch (error) {
      const reason = error instanceof ImagePresentationError ? error.reason : 'context'
      this.#enterRecovery(reason === 'asset' ? 'context' : reason)
    }
  }

  #referencesMissingFutureGrid(sequence: number): boolean {
    return sequence > Math.max(this.#model.sequence, this.#imagePresentation.pendingGridSequence)
  }

  async #renderLatest(): Promise<void> {
    if (this.#disposed) return
    this.#renderDirty = true
    if (this.#renderPump !== null) return
    const session = this.#renderSession
    const pump = this.#drainRenderPump(session)
    this.#renderPump = pump
    try {
      await pump
    } finally {
      if (this.#renderSession !== session || this.#renderPump !== pump) return
      this.#renderPump = null
      if (this.#renderDirty && !this.#disposed) void this.#renderLatest()
    }
  }

  async #drainRenderPump(session: number): Promise<void> {
    while (!this.#disposed && this.#renderSession === session && this.#renderDirty) {
      this.#renderDirty = false
      await this.#renderOnce()
    }
  }

  async #renderOnce(): Promise<void> {
    const active = this.#configuration
    if (this.#disposed || active === null || this.#model.sequence === 0 ||
        this.#model.configGeneration !== active.value.configGeneration) return
    const epoch = ++this.#renderEpoch
    try {
      await active.configure
      await Promise.all(Array.from(active.fontTasks))
      if (this.#disposed || this.#configuration !== active || epoch !== this.#renderEpoch ||
          !this.#fontStore.isReady(active.value.configGeneration)) return
      let imageGeometry: ImageGeometry | undefined
      try {
        const renderer = this.#renderer
        const scene = this.#imagePresentation.committedScene
        if (this.#imageFeatureReady && scene !== undefined &&
            this.#imageStore.acquireForRender !== undefined) {
          const assets: ImageGeometryAssetSource = Object.freeze({
            activeEpoch: this.#imageStore.activeEpoch,
            acquireForRender: (assetId: number) =>
              renderer.acquireCachedImage?.(assetId) ??
              this.#imageStore.acquireForRender!(assetId),
          })
          imageGeometry = buildImageGeometry(
            scene,
            assets,
            {
              cols: this.#model.cols,
              rows: this.#model.rows,
              cellWidth: active.value.cellWidth,
              cellHeight: active.value.cellHeight,
              viewportWidth: this.#cssWidth,
              viewportHeight: this.#cssHeight,
            },
            { retainSources: this.#rendererKind === 'canvas2d' },
          )
        }
        const images = imageGeometry?.draws ?? EMPTY_IMAGE_DRAWS
        const geometry = buildGeometry({
          cols: this.#model.cols,
          rows: this.#model.rows,
          cells: this.#model.cells,
          cursor: this.#model.cursor,
        }, active.value, {
          availableVariants: this.#fontStore.availableVariants(active.value.configGeneration),
          selection: this.#selection?.rect() ?? undefined,
          ...(imageGeometry === undefined ? {} : { suppression: imageGeometry.suppression }),
        })
        await renderer.render({
          geometry,
          images,
          config: active.value,
          cols: this.#model.cols,
          rows: this.#model.rows,
          dpr: safeDpr(this.#devicePixelRatio),
          viewportWidth: this.#cssWidth,
          viewportHeight: this.#cssHeight,
        })
      } finally {
        imageGeometry?.release()
      }
    } catch {
      if (this.#configuration === active && epoch === this.#renderEpoch) this.#enterRecovery('context')
    }
  }

  #enterRecovery(reason: GridRecoveryReason): void {
    this.#assembler.resetForRecovery()
    if (!this.#recovering) {
      this.#recovering = true
      this.#socket.sendGridRecover(this.#model.sequence, reason)
    }
    if (this.#imageFeatureReady) this.#enterImageRecovery(reason)
  }

  #enterImageRecovery(reason: GridImageRecoveryReason): void {
    if (this.#disposed) return
    this.#assembler.resetForRecovery()
    this.#invalidateImageDelivery()
    this.#imagePresentation.clearForRecovery()
    this.requestRepaint()
    if (this.#imageRecovering || this.#deferredImageRecoveryReason !== null ||
        !this.#imageFeatureReady || this.#assetEpoch === 0) return
    if (this.#imageStore.requestIdleNotification()) {
      this.#deferredImageRecoveryReason = reason
      return
    }
    this.#imageRecovering = true
    this.#socket.sendGridImageRecover(
      this.#assetEpoch,
      this.#imagePresentation.lastPresentationGeneration,
      reason,
    )
  }

  #setRole(role: GridRole | null): void {
    if (this.#role === role) return
    const wasController = this.#role === 'controller'
    this.#role = role
    // Losing control ends the interaction: an observer holds no pin, so the
    // rectangle it is drawing would sit over lines free to scroll away.
    if (wasController && role !== 'controller') {
      this.#pointer?.cancelFromServer()
      this.#selection?.invalidateFromServer()
    }
    this.#onRoleChange?.(role)
  }

  #observeResize(entries: readonly ResizeObserverEntry[]): void {
    const entry = entries[entries.length - 1]
    if (entry === undefined) return
    const width = Math.max(0, entry.contentRect.width)
    const height = Math.max(0, entry.contentRect.height)
    const viewportChanged = width !== this.#cssWidth || height !== this.#cssHeight
    this.#cssWidth = width
    this.#cssHeight = height
    const dprChanged = this.#updateDpr()
    if (dprChanged) this.#armDprObserver()
    if (viewportChanged || dprChanged) void this.#renderLatest()
    this.#scheduleResize()
  }

  #updateDpr(): boolean {
    const dpr = safeDpr(this.#devicePixelRatio)
    if (dpr === this.#lastDpr) return false
    this.#lastDpr = dpr
    return true
  }

  #armDprObserver(): void {
    this.#clearDprObserver()
    if (this.#disposed || typeof window.matchMedia !== 'function') return
    try {
      const query = window.matchMedia(`(resolution: ${this.#lastDpr}dppx)`)
      query.addEventListener('change', this.#handleDprMediaChange)
      this.#dprMediaQuery = query
    } catch {
      // Window resize remains a fallback when resolution media queries are unavailable.
    }
  }

  #clearDprObserver(): void {
    this.#dprMediaQuery?.removeEventListener('change', this.#handleDprMediaChange)
    this.#dprMediaQuery = null
  }

  #scheduleResize(): void {
    this.#cancelResize()
    if (this.#role !== 'controller' || this.#configuration === null ||
        this.#cssWidth <= 0 || this.#cssHeight <= 0) return
    this.#resizeTimer = window.setTimeout(() => {
      this.#resizeTimer = null
      const active = this.#configuration
      if (this.#role !== 'controller' || active === null) return
      const size = this.#gridSize(active.value)
      const key = `${active.value.configGeneration}:${size.cols}:${size.rows}`
      if (key === this.#lastResizeKey) return
      this.#lastResizeKey = key
      // The grid the interaction started on is about to stop existing, so
      // neither the drag nor the rectangle it measured survives the new
      // geometry.
      this.#pointer?.cancelInteraction()
      this.#selection?.invalidate('resize')
      this.#clearImagePresentationForSynchronization()
      this.#socket.sendGridResize(active.value.configGeneration, size.cols, size.rows)
    }, this.#resizeDebounceMs)
  }

  #cancelResize(): void {
    if (this.#resizeTimer === null) return
    clearTimeout(this.#resizeTimer)
    this.#resizeTimer = null
  }

  #gridSize(config: GridRendererConfig): { cols: number; rows: number } {
    return {
      cols: clamp(Math.floor(this.#cssWidth / config.cellWidth), MIN_COLS, MAX_COLS),
      rows: clamp(Math.floor(this.#cssHeight / config.cellHeight), MIN_ROWS, MAX_ROWS),
    }
  }

  #releasePointerCapture(): void {
    for (const pointerId of this.#activePointers) {
      try {
        if (this.#canvas.hasPointerCapture(pointerId)) this.#canvas.releasePointerCapture(pointerId)
      } catch {
        // Replacing the surface below also drops browser-owned capture.
      }
    }
    this.#activePointers.clear()
  }

  #clearImagePresentationForSynchronization(): void {
    if (!this.#imageFeatureReady) return
    this.#assembler.resetForRecovery()
    this.#invalidateImageDelivery()
    this.#imagePresentation.clearForRecovery()
    this.#imageCapacityBlocked = false
    this.requestRepaint()
  }
}
